<template>
  <article :class="['project', 'glass-card', { 'project--no-image': !image }]">
    <div v-if="image" class="project__media">
      <img :src="image" :alt="title" class="project__img" />
    </div>
    <div class="project__info">
      <div class="project__meta">
        <span class="project__chip project__chip--year">{{ year }}</span>
        <span class="project__chip project__chip--status">
          {{ inProgress ? $t("Project.inProgress") : $t("Project.finished") }}
        </span>
      </div>
      <h3 class="project__title">{{ title }}</h3>
      <p class="project__subtitle">{{ subtitle }}</p>
      <a v-if="link && action" :href="link" class="project__action">{{
        action
      }}</a>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "JroProject",
  props: {
    inProgress: Boolean,
    year: String,
    title: String,
    subtitle: String,
    action: String,
    link: String,
    image: String,
  },
});
</script>

<style lang="scss" scoped>
@use "../global.scss" as *;

.project {
  display: grid;
  grid-template-columns: minmax(0, 14rem) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;
  height: 100%;
  padding: 1.25rem;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-soft-lg);
  }
}

.project--no-image {
  grid-template-columns: minmax(0, 1fr);
}

.project__media {
  overflow: hidden;
  border-radius: calc(var(--radius) * 0.8);
  min-height: 9rem;
  background: var(--brand-icy);
  opacity: 0.85;
}

.project__img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 9rem;
  object-fit: cover;
}

.project__info {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0;
}

.project__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.project__chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.project__chip--year {
  background: color-mix(in srgb, var(--brand-icy) 45%, transparent);
  color: var(--foreground);
}

.project__chip--status {
  background: color-mix(in srgb, var(--brand-petal) 50%, transparent);
  color: var(--foreground);
}

.project__title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--foreground);
}

.project__subtitle {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--muted-foreground);
}

.project__action {
  width: fit-content;
  margin-top: 1rem;
  padding-bottom: 0.125rem;
  border-bottom: 1px solid var(--brand-primary);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--brand-primary);
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: var(--brand-secondary);
    border-color: var(--brand-secondary);
  }
}

@media (min-width: $bp-large) {
  @container (max-width: 42rem) {
    .project:not(.project--no-image) {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
      align-items: stretch;
      gap: 1.25rem;
    }

    .project__media {
      max-width: none;
      min-height: 0;
      aspect-ratio: 16 / 10;
    }

    .project__img {
      min-height: 0;
      height: auto;
      aspect-ratio: 16 / 10;
    }

    .project__info {
      flex: 1;
    }

    .project__subtitle {
      flex: 1;
    }

    .project--no-image {
      grid-template-rows: auto 1fr;
    }

    .project--no-image::before {
      content: "";
      aspect-ratio: 16 / 10;
    }
  }
}

@media (max-width: $bp-medium) {
  .project:not(.project--no-image) {
    grid-template-columns: minmax(0, 10rem) minmax(0, 1fr);
    gap: 1.25rem;
    align-items: start;
  }

  .project--no-image {
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
  }

  .project__media {
    min-height: 0;
  }

  .project__img {
    min-height: 0;
    aspect-ratio: 4 / 3;
  }
}
</style>
